// CACHE DOS PRESENTES EM VÍDEO
//
// O problema: o vídeo de um presente só começava a descarregar no instante em
// que o presente disparava. Com 1,5 MB por presente, num telemóvel isso são
// 1 a 3 segundos de nada no ecrã — e depois o presente aparece atrasado em
// relação ao aviso de "recebeste um presente".
//
// A solução tem duas partes:
//
//   1. AQUECER: mal se entra numa live/sala/arena, os presentes descarregam em
//      segundo plano, dois de cada vez, para a Cache API do navegador (fica em
//      disco, sobrevive a fechar a app, e os nomes dos ficheiros têm hash, por
//      isso nunca fica um presente velho). Quanto se descarrega depende da
//      ligação: tudo em wi-fi, só os primeiros em dados móveis, nada com
//      "poupar dados" ligado.
//
//   2. SERVIR: quando o presente dispara, o vídeo vem da cache como um blob:
//      URL. Um blob: URL toca de imediato e suporta os pedidos por intervalos
//      (Range) que o Safari exige para vídeo — coisa que servir da cache pelo
//      service worker NÃO garante. Se ainda não está na cache, toca da rede
//      como antes (e fica na cache para a próxima).

export const NOME_CACHE = 'gamefut-presentes-v1';

// Quantos presentes aquecer por tipo de ligação. Os ficheiros têm ~1,5 MB.
export const LIMITES = {
  'poupar-dados': 0,
  'lenta': 6,        // 2g / 3g
  'movel': 15,       // 4g ou desconhecida (pode ser dados móveis)
  'wifi': Infinity,
};

// Classifica a ligação a partir do que o navegador diz (nem todos dizem).
export function tipoDeLigacao(ligacao = globalThis.navigator?.connection) {
  if (!ligacao) return 'movel';
  if (ligacao.saveData) return 'poupar-dados';
  const tipo = String(ligacao.type || '').toLowerCase();
  if (tipo === 'wifi' || tipo === 'ethernet') return 'wifi';
  const efetivo = String(ligacao.effectiveType || '').toLowerCase();
  if (efetivo === 'slow-2g' || efetivo === '2g' || efetivo === '3g') return 'lenta';
  return 'movel';
}

// Quantos aquecer para esta ligação.
export function quantosAquecer(tipo = tipoDeLigacao()) {
  return LIMITES[tipo] ?? LIMITES.movel;
}

// Ordena os urls: primeiro os que vêm na lista de prioridade (os mais
// baratos, que são os mais enviados), depois os restantes na ordem dada.
export function ordenarPorPrioridade(urls, prioridade = []) {
  const posicao = new Map(prioridade.map((u, i) => [u, i]));
  return [...urls].sort((a, b) => {
    const pa = posicao.has(a) ? posicao.get(a) : Infinity;
    const pb = posicao.has(b) ? posicao.get(b) : Infinity;
    return pa - pb;
  });
}

const temCache = () => typeof caches !== 'undefined' && typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function';

// blob: URLs já criados nesta sessão, por url original. Um blob: URL vale até
// a página fechar; criar um novo a cada presente deixava lixo em memória.
const blobs = new Map();
const emCurso = new Map();

async function abrirCache() {
  try { return await caches.open(NOME_CACHE); } catch { return null; }
}

// Descarrega um presente para a cache (se ainda não estiver). Nunca atira.
export async function guardarPresente(url) {
  if (!temCache() || !url) return false;
  const cache = await abrirCache();
  if (!cache) return false;
  try {
    if (await cache.match(url)) return true;
    const resposta = await fetch(url);
    if (!resposta.ok) return false;
    await cache.put(url, resposta);
    return true;
  } catch {
    return false;
  }
}

// O url a dar ao <video>: um blob: URL se o presente estiver na cache, senão o
// próprio url. Em paralelo garante que fica na cache para a próxima vez.
export async function urlRapido(url) {
  if (!url || !temCache()) return url;
  if (blobs.has(url)) return blobs.get(url);
  if (emCurso.has(url)) return emCurso.get(url);

  const promessa = (async () => {
    try {
      const cache = await abrirCache();
      const guardado = cache && await cache.match(url);
      if (!guardado) {
        // Não espera pelo download: toca da rede já, e a cache enche ao lado.
        guardarPresente(url);
        return url;
      }
      const blob = await guardado.blob();
      const blobUrl = URL.createObjectURL(blob);
      blobs.set(url, blobUrl);
      return blobUrl;
    } catch {
      return url;
    } finally {
      emCurso.delete(url);
    }
  })();
  emCurso.set(url, promessa);
  return promessa;
}

// Aquece a cache: descarrega os presentes em segundo plano, `paralelos` de
// cada vez, até ao limite da ligação. Devolve uma função para parar (o ecrã
// chama-a ao desmontar; o que já estiver descarregado fica).
export function aquecerPresentes(urls, { prioridade = [], limite = quantosAquecer(), paralelos = 2, guardar = guardarPresente, doc = globalThis.document } = {}) {
  if (!temCache() || !urls?.length || limite <= 0) return () => {};

  const fila = ordenarPorPrioridade(urls, prioridade).slice(0, limite);
  let parado = false;

  const proximo = async () => {
    while (!parado && fila.length) {
      // Com a aba escondida (telemóvel bloqueado) espera, em vez de gastar
      // dados às escuras.
      if (doc && doc.visibilityState === 'hidden') {
        await new Promise((r) => setTimeout(r, 2000));
        continue;
      }
      const url = fila.shift();
      await guardar(url);
    }
  };

  const trabalhadores = Array.from({ length: Math.max(1, paralelos) }, () => proximo());
  Promise.allSettled(trabalhadores);

  return () => { parado = true; fila.length = 0; };
}
